import { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import MasterDataTable from '@/components/MasterDataTable/index'
import Search from '@/components/Search/index'
import IconInfoEmpty from '@/assets/svgs/Icon-info.svg?react'
import { ToastContext } from '@/context/ToastContext'
import { downloadSubmittalAsCsv } from './utils/downloadSubmittalAsCsv'
import FilterBar from './components/FilterBar'
import SubmittalStatusTag from './components/SubmittalStatusTag'
import useFilterOptions from '@/hooks/useFilterOptions'
import useListingTabState from '@/hooks/useListingTabState'
import useSubmittals from '@/hooks/useSubmittals'
import { formatDateTime } from '@/utils/formatDate'
import {
  COLUMN_TYPE,
  FILTER_TYPE,
  buildFilterParams,
  payloadGroupsFromColumns,
  valueAtKey,
} from './listing-config'

const EMPTY_CELL = '-'
const SEARCH_DEBOUNCE_MS = 400

const isEmpty = value => value === undefined || value === null || value === ''

// Stable identity for "options not known yet": availableFilters memoises on it.
const NO_FILTER_OPTIONS = {}

/**
 * A listing drawn from a data element's `listingConfig`: its columns, its
 * filters, its sort.
 *
 * Ported from pitstop-ui's submittals-tab-listing. What is deliberately absent
 * is the submitter's row menu — Edit and Remove act on a draft, which a
 * consultant never sees, since `GET /consultant/submittals` excludes DRAFT. The
 * download action is kept, as the V1 listing has it.
 */
const SubmittalsTabListing = ({ dataElementId, columns = [], filters = [], secondaryApproval }) => {
  const navigate = useNavigate()
  const listingTabState = useListingTabState()
  const { addToastMessage } = useContext(ToastContext)

  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  // No default sort: the config does not express one, and inheriting the V1
  // listing's `modified` would send a sort column this data element may not have.
  const [sortConfig, setSortConfig] = useState({ key: null, ascending: true })
  // The current selection per filter; the options come from useFilterOptions.
  const [selections, setSelections] = useState({})
  const [downloading, setDownloading] = useState(false)

  // A sort column or a filter selection from one data element means nothing to
  // the next, so a tab switch drops both. The search term is deliberately kept,
  // as pitstop keeps it: it is free text, so carrying it across is how a
  // consultant chases one contract number through the tabs.
  //
  // Done while rendering rather than in an effect, because the request is built
  // from this state during the same render: reset afterwards, the tab switch
  // would first fire one request sorting the new data element by a column only
  // the old one has.
  const [renderedTab, setRenderedTab] = useState(dataElementId)
  if (renderedTab !== dataElementId) {
    setRenderedTab(dataElementId)
    setSortConfig({ key: null, ascending: true })
    setSelections({})
    setCurrentPage(1)
  }

  // Rows are never requested before this tab's config is available: the column
  // definitions are what the request is derived from.
  const isReady = columns.length > 0

  // Scoped to this tab's data element: unscoped lists would offer values that
  // only exist on another tab's submittals, which select to an empty table.
  const filterData = useFilterOptions(!!dataElementId, dataElementId) || NO_FILTER_OPTIONS

  // A filter whose options have not arrived is not rendered, so an entry can be
  // authored before the backend supplies its list. Date ranges have no options.
  const availableFilters = useMemo(
    () =>
      filters.filter(
        filter =>
          filter.type === FILTER_TYPE.DATE_RANGE || Array.isArray(filterData[filter.optionsFrom])
      ),
    [filters, filterData]
  )

  const filterParams = useMemo(
    () => buildFilterParams(availableFilters, selections),
    [availableFilters, selections]
  )

  const payloadFields = useMemo(() => payloadGroupsFromColumns(columns), [columns])

  const { submittals, meta, loading, error, pageSize } = useSubmittals(isReady, {
    page: currentPage,
    search,
    dataElementId,
    // The column key doubles as the backend sort column. Only columns the config
    // marks sortable can get here.
    sort: sortConfig.key || undefined,
    order: sortConfig.key ? (sortConfig.ascending ? 'asc' : 'desc') : undefined,
    extraParams: filterParams,
    payloadFields,
  })

  // Debounce search input.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Page 1 for a different set of rows. The tab switch is handled above instead,
  // for the same reason it resets the sort there.
  useEffect(() => {
    setCurrentPage(1)
  }, [search, sortConfig, filterParams])

  // A column's key is the field on the row; a dotted key reads a nested one.
  const renderCell = (column, row) => {
    const value = valueAtKey(row, column.key)

    if (column.type === COLUMN_TYPE.STATUS_TAG) {
      return <SubmittalStatusTag status={value} secondaryApproval={secondaryApproval} />
    }

    if (isEmpty(value)) return EMPTY_CELL

    if (column.type === COLUMN_TYPE.DATE) {
      // Stored UTC, shown in the viewer's own zone. The backend still resolves
      // date filters against SGT, so a viewer elsewhere can see a row their own
      // filter misses.
      //
      // The shared formatter, so the detail page this row links to cannot print
      // the same timestamp a different way. Already-formatted values pass
      // through rather than becoming "Invalid date".
      return formatDateTime(value)
    }

    return typeof value === 'object' ? EMPTY_CELL : String(value)
  }

  const tableColumns = useMemo(
    () => [
      ...columns.map(column => ({
        key: column.key,
        label: column.label,
        sortable: column.sortable,
        render: row => renderCell(column, row),
      })),
      // Not a configured column — the per-row download button, same as the V1
      // listing offers.
      { key: 'action', label: '' },
    ],
    [columns, secondaryApproval]
  )

  const handleSort = key => {
    const column = columns.find(candidate => candidate.key === key)
    if (!column?.sortable) return
    setSortConfig(prev => ({
      key,
      ascending: prev.key === key ? !prev.ascending : true,
    }))
  }

  const handleDownloadCsv = async row => {
    setDownloading(true)
    const { error: downloadError } = await downloadSubmittalAsCsv(
      row.submittal_id,
      row.data_element_id || dataElementId
    )
    setDownloading(false)
    if (downloadError) addToastMessage(downloadError, 'error', 'dismiss')
  }

  return (
    <>
      <FilterBar
        filters={availableFilters}
        filterData={filterData}
        selections={selections}
        onSelect={(key, value) => setSelections(prev => ({ ...prev, [key]: value }))}
        secondaryApproval={secondaryApproval}
      />

      {/* 20px above and below, matching pitstop, where the two are deliberately
          equal ("Match gap between table and search bar to gap between filters
          and search bar" in _submittals.scss). The filter row has no bottom
          margin of its own, so the upper gap is set here.
          Width is pitstop's fixed 300px, going full-width on a phone where 300px
          would overflow the viewport. */}
      <div className="mb-5 mt-5 w-[300px] max-phone:w-full">
        <Search
          value={searchInput}
          onChange={setSearchInput}
          onCancel={() => {
            setSearchInput('')
            setSearch('')
          }}
          id="submittals-tab-listing-search"
        />
      </div>

      <MasterDataTable
        columns={tableColumns}
        data={submittals}
        currentPage={currentPage}
        totalPages={meta.totalPages}
        totalItems={meta.total}
        // The size that was actually requested, so the pager's "1-100 of 240"
        // cannot disagree with the rows under it.
        pageLimit={pageSize}
        onPageChange={setCurrentPage}
        onSort={handleSort}
        sortColumn={sortConfig.key}
        sortOrder={sortConfig.ascending ? 'asc' : 'desc'}
        // The data element goes with it, so the detail page's back controls
        // return to this listing rather than the first one, and the sidebar keeps
        // this element highlighted while the submittal is open.
        onRowClick={row =>
          navigate(`/submittals/${row.submittal_id}`, { state: listingTabState(dataElementId) })
        }
        onDownloadCsv={handleDownloadCsv}
        loading={loading || downloading}
        // A failed fetch has its own slot rather than being folded into the
        // empty message: "no submissions" is a statement about this data
        // element, and printing it when the request never came back tells the
        // consultant something the app does not know.
        errorMessage={error}
        // Deliberately not pitstop's "No new submission created yet." — that
        // wording is the submitter's, who creates submissions; this app's reader
        // only ever finds them.
        emptyMessage="No new submissions found."
        emptyIcon={<IconInfoEmpty width={32} height={32} />}
      />
    </>
  )
}

SubmittalsTabListing.propTypes = {
  dataElementId: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      type: PropTypes.string,
      sortable: PropTypes.bool,
    })
  ),
  filters: PropTypes.array,
  secondaryApproval: PropTypes.shape({
    required: PropTypes.bool,
    label: PropTypes.string,
  }),
}

export default SubmittalsTabListing
